"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  OaResultTable,
  type OaRow,
  oaTsv,
} from "@/components/tools/reference-tools/oa-table";
import { useRun } from "@/components/tools/reference-tools/use-run";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { fetchJson, runPool } from "@/lib/tool-kit/reference-tools/network";
import {
  OA_STATUSES,
  type OaRecord,
  openAlexBatchUrl,
  openAlexWorkUrl,
  parseOpenAlexList,
  parseOpenAlexWork,
  planOpenAlex,
  readDoiLines,
  statusCounts,
} from "@/lib/tool-kit/reference-tools/open-access";

/** English strings. A translated content file overrides these through `ui`. */
export const OPEN_ACCESS_FINDER_STRINGS = {
  inputLabel:
    "DOIs — one per line (a reference list works too: the DOI in each line is used)",
  inputPlaceholder:
    "10.1073/pnas.0507655102\nhttps://doi.org/10.1371/journal.pone.0115253\ndoi:10.2307/1914185",
  inputHint:
    "Up to {max} DOIs per run, looked up on OpenAlex — fifty to a request — from your browser.",
  check: "Check open access",
  checking: "Looking up {done} of {total}…",
  cancel: "Stop",
  emptyPrompt: "Paste DOIs and press Check open access.",
  unexpected: "Something went wrong during the lookup. Please try again.",
  resultsHeading: "Open-access status",
  summary: "one:{count} DOI looked up|other:{count} DOIs looked up",
  openCount: "{open} with a free copy OpenAlex knows of",
  copyTable: "Copy table",
  copied: "Copied!",
  indexNote:
    "Statuses come from OpenAlex and cover only the copies it has indexed — “closed” means no free copy OpenAlex knows of, not that none exists.",
  columns: {
    doi: "DOI",
    work: "Work",
    status: "Status",
    copy: "Best free copy",
    details: "Version · licence · host",
  },
  statuses: {
    diamond: "Diamond",
    gold: "Gold",
    hybrid: "Hybrid",
    bronze: "Bronze",
    green: "Green",
    closed: "Closed",
  },
  statusMeaning: {
    diamond: "open in a journal that charges neither readers nor authors",
    gold: "open in a fully open-access journal",
    hybrid: "open under a licence in a subscription journal",
    bronze: "free to read on the publisher's site, with no open licence",
    green: "a free copy in a repository; the publisher's version is paywalled",
    closed: "no free copy OpenAlex knows of",
  },
  rowStates: {
    missing: "Not in OpenAlex",
    invalid: "No DOI in this line",
    duplicate: "Repeats an earlier line",
    over: "Over the limit — run it in a second batch",
    failed: "Lookup failed — try again",
    queued: "Waiting",
  },
  landingPage: "Article page",
  pdf: "PDF",
  moreCopies: "one:+{count} more open copy|other:+{count} more open copies",
  versions: {
    publishedVersion: "Published version",
    acceptedVersion: "Accepted manuscript",
    submittedVersion: "Submitted version (preprint)",
  },
  noVersion: "Version not stated",
  noLicense: "No licence listed",
  licenses: {
    "cc-by": "CC BY",
    "cc-by-sa": "CC BY-SA",
    "cc-by-nd": "CC BY-ND",
    "cc-by-nc": "CC BY-NC",
    "cc-by-nc-sa": "CC BY-NC-SA",
    "cc-by-nc-nd": "CC BY-NC-ND",
    cc0: "CC0",
    "public-domain": "Public domain",
    "other-oa": "Another open licence",
    "publisher-specific-oa": "The publisher's own open terms",
  },
} as const;

export type OpenAccessStrings = typeof OPEN_ACCESS_FINDER_STRINGS;

const MAX_DOIS = 100;

type Task = { kind: "batch"; dois: string[] } | { kind: "single"; doi: string };

function planRows(text: string): OaRow[] {
  let queued = 0;
  return readDoiLines(text).map((line): OaRow => {
    if (line.doi === null) {
      return { ...line, state: "invalid" };
    }
    if (line.duplicate) {
      return { ...line, state: "duplicate" };
    }
    queued += 1;
    return { ...line, state: queued > MAX_DOIS ? "over" : "queued" };
  });
}

function tasksFor(rows: readonly OaRow[]): Task[] {
  const dois = rows.flatMap((row) =>
    row.state === "queued" && row.doi ? [row.doi] : []
  );
  const { batches, singles } = planOpenAlex(dois);
  return [
    ...batches.map((batch): Task => ({ kind: "batch", dois: batch })),
    ...singles.map((doi): Task => ({ kind: "single", doi })),
  ];
}

/** Records for a task's DOIs; a DOI missing from the map was not in OpenAlex. */
async function lookUp(
  task: Task,
  signal: AbortSignal
): Promise<{ records: Map<string, OaRecord>; failed: boolean }> {
  const url =
    task.kind === "batch"
      ? openAlexBatchUrl(task.dois)
      : openAlexWorkUrl(task.doi);
  const response = await fetchJson<unknown>(url, { signal, retries: 1 });
  if (!response.ok) {
    return { records: new Map(), failed: response.reason !== "not-found" };
  }
  if (task.kind === "batch") {
    return { records: parseOpenAlexList(response.value), failed: false };
  }
  const record = parseOpenAlexWork(response.value);
  return {
    records: new Map(record ? [[task.doi, record]] : []),
    failed: false,
  };
}

function applyResult(
  rows: OaRow[],
  task: Task,
  result: { records: Map<string, OaRecord>; failed: boolean }
): OaRow[] {
  const dois = new Set(task.kind === "batch" ? task.dois : [task.doi]);
  return rows.map((row) => {
    if (row.state !== "queued" || !row.doi || !dois.has(row.doi)) {
      return row;
    }
    const record = result.records.get(row.doi);
    if (record) {
      return { ...row, state: "found", record };
    }
    return { ...row, state: result.failed ? "failed" : "missing" };
  });
}

export default function OpenAccessFinderWidget() {
  const strings = useToolUi(OPEN_ACCESS_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [rows, setRows] = useState<OaRow[]>([]);
  const [failed, setFailed] = useState(false);
  const run = useRun();

  async function handleCheck() {
    const planned = planRows(text);
    setRows(planned);
    setFailed(false);
    const tasks = tasksFor(planned);
    const controller = run.start(tasks.length);
    try {
      await runPool(
        tasks,
        async (task) => {
          const result = await lookUp(task, controller.signal);
          if (run.isCurrent(controller)) {
            setRows((current) => applyResult(current, task, result));
          }
        },
        {
          concurrency: 2,
          signal: controller.signal,
          onProgress: (done) => run.advance(controller, done, tasks.length),
        }
      );
    } catch {
      if (run.isCurrent(controller)) {
        setFailed(true);
      }
    } finally {
      run.finish(controller);
    }
  }

  const found = rows.flatMap((row) => (row.record ? [row.record] : []));
  const counts = statusCounts(found);
  const open = found.length - counts.closed;
  const lookedUp = rows.filter((row) =>
    ["found", "missing", "failed"].includes(row.state)
  ).length;

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={t(strings.inputHint, { max: fmt(MAX_DOIS) })}
        id="oaf-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={7}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={run.running || text.trim() === ""}
          onClick={handleCheck}
          type="button"
        >
          {run.progress
            ? t(strings.checking, {
                done: fmt(run.progress.done),
                total: fmt(run.progress.total),
              })
            : strings.check}
        </button>
        {run.running ? (
          <button
            className={BUTTON_SECONDARY}
            onClick={run.cancel}
            type="button"
          >
            {strings.cancel}
          </button>
        ) : null}
      </div>
      <div aria-live="polite" className="space-y-6">
        {failed ? <ErrorNotice>{strings.unexpected}</ErrorNotice> : null}
        {rows.length === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {rows.length > 0 ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.summary, locale, lookedUp, {
                    count: fmt(lookedUp),
                  })}
                  {" · "}
                  {t(strings.openCount, { open: fmt(open) })}
                </p>
              </div>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyTable}
                text={oaTsv(rows, strings)}
              />
            </div>
            <ul className="flex flex-wrap gap-2 text-sm">
              {OA_STATUSES.map((status) => (
                <li
                  className="rounded-md border border-border bg-card px-2 py-1"
                  key={status}
                >
                  <span className="font-medium text-foreground">
                    {strings.statuses[status]}
                  </span>{" "}
                  <span className="text-muted-foreground">
                    {fmt(counts[status])}
                  </span>
                </li>
              ))}
            </ul>
            <OaResultTable rows={rows} strings={strings} />
            <p className={HINT}>{strings.indexNote}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
